import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../state/language/LanguageProvider';
import { useAuth } from '../../state/auth/AuthProvider';
import { PublicHeader } from '@/components/public/PublicHeader';
import templateService from '../../services/templateService';
import { roadmapMarketplaceService } from '../../services/roadmapMarketplace';
import api from '../../services/api';
import { 
  EyeIcon, 
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  StarIcon
} from '@heroicons/react/24/outline';

// =====================================================
// ROADMAP MARKETPLACE PAGE
// TMOS Platform - Core Feature Page
// =====================================================

// Template interface for the new JSONB structure
interface Template {
  id: string;
  name: string;
  description?: string;
  template_type: string;
  content: {
    template_metadata: {
      name: string;
      description: string;
      industry: string;
      functional_area: string;
      complexity_level: number;
      estimated_duration_months: number;
      tags: string[];
    };
    hierarchy: {
      visions: Array<{
        title: string;
        description: string;
        priority: string;
        target_date?: string;
        success_criteria: string[];
        objectives: Array<{
          title: string;
          description: string;
          priority: string;
          strategies: Array<{
            title: string;
            description: string;
            priority: string;
            projects: Array<{
              title: string;
              description: string;
              priority: string;
              estimated_duration_months: number;
              estimated_cost?: number;
            }>;
          }>;
        }>;
      }>;
    };
  };
  marketplace_visibility?: 'DRAFT' | 'PRIVATE' | 'PUBLIC';
  created_by?: string;
  is_public?: boolean;
  is_published?: boolean;
  created_at: string;
  updated_at: string;
  tenant_name?: string | null;
}

export const RoadmapMarketplacePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user, isAuthenticated } = useAuth();
  
  // State for templates and search
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredTemplates, setFilteredTemplates] = useState<Template[]>([]);
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [selectedFunctionalArea, setSelectedFunctionalArea] = useState('');
  const [availableIndustries, setAvailableIndustries] = useState<string[]>([]);
  const [availableFunctionalAreas, setAvailableFunctionalAreas] = useState<string[]>([]);
  const [savingTemplate, setSavingTemplate] = useState<string | null>(null);
  const [templateAccess, setTemplateAccess] = useState<Record<string, boolean>>({}); // Track access for each template
  

  // =====================================================
  // DATA LOADING
  // =====================================================
  
  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    // Filter templates based on search term, template type, industry, and functional area
    let filtered = templates;
    
    // Apply search filter
    if (searchTerm.trim() !== '') {
      filtered = filtered.filter(template => 
        template.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.content?.template_metadata?.industry?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.content?.template_metadata?.functional_area?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Template type filter removed - marketplace only shows ROADMAP templates (filtered by backend)
    
    // Apply industry filter
    if (selectedIndustry !== '') {
      filtered = filtered.filter(template => template.content?.template_metadata?.industry === selectedIndustry);
    }
    
    // Apply functional area filter
    if (selectedFunctionalArea !== '') {
      filtered = filtered.filter(template => template.content?.template_metadata?.functional_area === selectedFunctionalArea);
    }
    
    setFilteredTemplates(filtered);
  }, [searchTerm, selectedIndustry, selectedFunctionalArea, templates]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      
      // Use marketplace endpoint to get templates visible in marketplace (PUBLIC and PRIVATE)
      // Remove hardcoded template_type filter to show all template types
      const response = await api.get('/templates/marketplace');
      
      if (response.data.success) {
        const templatesData = response.data.data?.templates || response.data.data || [];
                  
        setTemplates(templatesData);
        setFilteredTemplates(templatesData);
      
        // Extract unique industries and functional areas from template metadata
        const industries = [...new Set(templatesData.map((t: any) => t.content?.template_metadata?.industry).filter(Boolean))].sort() as string[];
        const functionalAreas = [...new Set(templatesData.map((t: any) => t.content?.template_metadata?.functional_area).filter(Boolean))].sort() as string[];
        
        // Note: We use allTemplateTypes constant instead of extracting from data
        // This ensures all template types are available in the dropdown, even if none exist yet
        setAvailableIndustries(industries);
        setAvailableFunctionalAreas(functionalAreas);

        // Check access for private templates
        if (user?.id) {
          const accessChecks: Record<string, boolean> = {};
          
          // Check access for all private templates in parallel
          const privateTemplates = templatesData.filter((t: any) => t.marketplace_visibility === 'PRIVATE');
          const accessPromises = privateTemplates.map(async (template: any) => {
            // Check if user is the owner
            if (template.created_by === user.id) {
              accessChecks[template.id] = true;
              return;
            }
            
            // Check if user has been granted access
            try {
              const hasAccess = await templateService.checkAccess(template.id);
              accessChecks[template.id] = hasAccess;
            } catch (err) {
              console.error(`Error checking access for template ${template.id}:`, err);
              accessChecks[template.id] = false;
            }
          });
          
          await Promise.all(accessPromises);
          setTemplateAccess(accessChecks);
        }
      } else {
        throw new Error(response.data.error || t('marketplace.errors.failedToLoadTemplates'));
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // EVENT HANDLERS
  // =====================================================

  const handleView = (template: Template) => {
    if (!user) {
      navigate('/', { state: { returnTo: `/marketplace/${template.id}` } });
      return;
    }
    navigate(`/marketplace/${template.id}`);
  };

  const handleSaveToMyRoadmaps = async (template: Template) => {
    if (!user?.tenantId || !user?.id) {
      alert(t('marketplace.errors.mustBeLoggedInToSave'));
      return;
    }

    try {
      setSavingTemplate(template.id);

      await roadmapMarketplaceService.downloadRoadmap(template.id);

      alert(t('marketplace.success.templateSavedToWorkspace'));
      navigate('/tenant/roadmaps');
    } catch (error) {
      console.error('Error saving template to workspace:', error);
      alert(error instanceof Error ? error.message : t('marketplace.errors.failedToSaveTemplate'));
    } finally {
      setSavingTemplate(null);
    }
  };

  const getPublisherDisplay = (template: Template) => {
    const metadata = template.content?.template_metadata as any;
    const publisher =
      template.tenant_name?.trim() ||
      metadata?.tenant_name?.trim?.() ||
      metadata?.publisher?.trim?.();

    if (publisher) {
      return publisher;
    }
    return t('roadmapDetail.unknownPublisher') || 'Publisher not specified';
  };



  // =====================================================
  // RENDER
  // =====================================================

  // If authenticated, render without PublicHeader/PublicFooter (Layout provides navigation)
  // If not authenticated, render with PublicHeader/PublicFooter
  if (isAuthenticated) {
    return (
      <div className="min-h-screen flex flex-col bg-[var(--color-secondary-bg)]">
        <main className="flex-1">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-[var(--color-surface-text)] mb-2">{t('marketplace.title')}</h1>
            <p className="text-lg text-[var(--color-secondary-text)]">
              {t('roadmapMarketplace.subtitle')}
            </p>
          </div>

          {/* Main Content */}
          <div>
          {/* Search and Filters */}
          <div className="mb-6">
            <div className="flex flex-wrap gap-4 items-end">
              {/* Search Bar */}
              <div className="relative flex-1 min-w-64 max-w-md">
                <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-1">
                  {t('marketplace.search')}
                </label>
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[var(--color-secondary-text)] opacity-50" />
                  <input
                    type="text"
                    placeholder={t('roadmapMarketplace.searchTemplates')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:ring-2 focus:ring-[var(--color-accent-bg)] focus:border-transparent"
                  />
                </div>
              </div>
              
              {/* Template Type Filter - HIDDEN: Marketplace only shows ROADMAP templates */}
              {/* <div className="flex-1 min-w-48">
                <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-1">
                  {t('marketplace.templateType')}
                </label>
                <select
                  value={selectedTemplateType}
                  onChange={(e) => setSelectedTemplateType(e.target.value)}
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:ring-2 focus:ring-[var(--color-accent-bg)] focus:border-transparent"
                >
                  <option value="">{t('marketplace.allTypes')}</option>
                  {allTemplateTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div> */}
              
              {/* Industry Filter */}
              <div className="flex-1 min-w-48">
                <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-1">
                  {t('roadmapMarketplace.filterByIndustry')}
                </label>
                <select
                  value={selectedIndustry}
                  onChange={(e) => setSelectedIndustry(e.target.value)}
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:ring-2 focus:ring-[var(--color-accent-bg)] focus:border-transparent"
                >
                  <option value="">{t('roadmapMarketplace.allIndustries')}</option>
                  {availableIndustries.map((industry) => (
                    <option key={industry} value={industry}>
                      {industry}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Functional Area Filter */}
              <div className="flex-1 min-w-48">
                <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-1">
                  {t('roadmapMarketplace.filterByFunctionalArea')}
                </label>
                <select
                  value={selectedFunctionalArea}
                  onChange={(e) => setSelectedFunctionalArea(e.target.value)}
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:ring-2 focus:ring-[var(--color-accent-bg)] focus:border-transparent"
                >
                  <option value="">{t('roadmapMarketplace.allFunctionalAreas')}</option>
                  {availableFunctionalAreas.map((area) => (
                    <option key={area} value={area}>
                      {area}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Clear Filters Button */}
              {(selectedIndustry !== '' || selectedFunctionalArea !== '' || searchTerm !== '') && (
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedIndustry('');
                      setSelectedFunctionalArea('');
                    }}
                    className="px-4 py-2 text-sm text-[var(--color-secondary-text)] hover:text-[var(--color-surface-text)] border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-accent-bg)]/20 transition-colors"
                  >
                    {t('roadmapMarketplace.clearFilters')}
                  </button>
                </div>
              )}
            </div>
          </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-[var(--color-secondary-text)] text-lg mb-4">{t('roadmapMarketplace.noTemplatesFound')}</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredTemplates.map((template) => {
              const metadata = template.content?.template_metadata;
              const hierarchy = template.content?.hierarchy;
              
              // Recursively count all projects including nested ones
              const countProjectsRecursively = (projects: any[]): number => {
                if (!projects || !Array.isArray(projects)) return 0;
                return projects.reduce((total, project) => {
                  const nestedCount = project.projects && Array.isArray(project.projects) 
                    ? countProjectsRecursively(project.projects) 
                    : 0;
                  return total + 1 + nestedCount; // Count this project + all nested projects
                }, 0);
              };
              
              const projectCount = hierarchy?.visions?.reduce((total, vision) => 
                total + vision.objectives?.reduce((objTotal, objective) => 
                  objTotal + objective.strategies?.reduce((strTotal, strategy) => 
                    strTotal + countProjectsRecursively(strategy.projects || []), 0), 0), 0) || 0;

              // Determine border color based on marketplace visibility
              const borderColor = template.marketplace_visibility === 'PUBLIC' 
                ? 'border-t-4 border-t-green-500' 
                : template.marketplace_visibility === 'PRIVATE'
                ? 'border-t-4 border-t-orange-500'
                : 'border-t-4 border-t-gray-300'; // Default for DRAFT or undefined

              return (
                <div
                  key={template.id}
                  className={`border border-[var(--color-border)] ${borderColor} rounded-lg hover:shadow-md transition-shadow cursor-pointer`}
                  onClick={() => handleView(template)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleView(template);
                    }
                  }}
                >
                  {/* Template Header */}
                  <div className={`p-4 border-b ${
                    template.marketplace_visibility === 'PUBLIC' 
                      ? 'border-b-green-200' 
                      : template.marketplace_visibility === 'PRIVATE'
                      ? 'border-b-orange-200'
                      : 'border-b-gray-100'
                  }`}>
                    <h3 className="font-semibold text-lg text-[var(--color-surface-text)] mb-2 line-clamp-2 hover:text-[var(--color-accent-bg)] transition-colors">
                      {template.name}
                    </h3>
                    <p className="text-[var(--color-secondary-text)] text-sm line-clamp-3">
                      {template.description || metadata?.description}
                    </p>
                    <div className="text-sm text-[var(--color-secondary-text)] mt-3">
                      <span className="font-medium text-[var(--color-surface-text)]">
                        {t('roadmapMarketplace.publisher') || 'Publisher'}:
                      </span>{' '}
                      {getPublisherDisplay(template)}
                    </div>
                  </div>

                  {/* Template Details */}
                  <div className="p-4">
                    <div className="space-y-2 text-sm">
                      {metadata?.industry && (
                        <div className="flex items-center">
                          <span className="text-[var(--color-secondary-text)] opacity-75 w-20">{t('roadmapMarketplace.industry')}</span>
                          <span className="text-[var(--color-surface-text)]">{metadata.industry}</span>
                        </div>
                      )}
                      {metadata?.functional_area && (
                        <div className="flex items-center">
                          <span className="text-[var(--color-secondary-text)] opacity-75 w-20">{t('roadmapMarketplace.area')}</span>
                          <span className="text-[var(--color-surface-text)]">{metadata.functional_area}</span>
                        </div>
                      )}
                      <div className="flex items-center">
                        <span className="text-[var(--color-secondary-text)] opacity-75 w-20">{t('roadmapMarketplace.complexity')}</span>
                        <span className="text-[var(--color-surface-text)]">{metadata?.complexity_level || t('common.notAvailable') || 'N/A'}/5</span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-[var(--color-secondary-text)] opacity-75 w-20">{t('roadmapMarketplace.projects')}</span>
                        <span className="text-[var(--color-surface-text)] font-medium">{projectCount}</span>
                      </div>
                      {metadata?.estimated_duration_months && (
                        <div className="flex items-center">
                          <span className="text-[var(--color-secondary-text)] opacity-75 w-20">{t('roadmapMarketplace.duration')}</span>
                          <span className="text-[var(--color-surface-text)]">{metadata.estimated_duration_months} {t('roadmapMarketplace.months')}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Template Actions */}
                  <div className="p-4 border-t border-[var(--color-border)] flex justify-between items-center">
                    {user ? (
                      <>
                        <div className="flex space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleView(template);
                            }}
                            className="p-2 text-[var(--color-secondary-text)] hover:text-[var(--color-accent-bg)] hover:bg-[var(--color-accent-bg)]/20 rounded-lg transition-colors"
                            title="View"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          {/* Only show download button if template is PUBLIC, or PRIVATE with access, or user is owner */}
                          {(template.marketplace_visibility === 'PUBLIC' || 
                            (template.marketplace_visibility === 'PRIVATE' && (template.created_by === user.id || templateAccess[template.id])) ||
                            !template.marketplace_visibility) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSaveToMyRoadmaps(template);
                              }}
                              disabled={savingTemplate === template.id}
                              className="p-2 text-[var(--color-secondary-text)] hover:text-[var(--color-success)] hover:bg-[var(--color-success)]/20 rounded-lg transition-colors disabled:opacity-50"
                              title={t('roadmapDetail.saveToMyRoadmaps')}
                            >
                              {savingTemplate === template.id ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-500"></div>
                              ) : (
                                <ArrowDownTrayIcon className="h-4 w-4" />
                              )}
                            </button>
                          )}
                        </div>
                        
                        <div className="flex items-center text-yellow-500">
                          <StarIcon className="h-4 w-4" />
                          <span className="text-sm font-medium ml-1">4.5</span>
                        </div>
                      </>
                    ) : (
                      <div className="w-full text-center">
                        <p className="text-sm text-[var(--color-secondary-text)]">
                          Login required to view and download
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </div>
        </div>
      </main>
    </div>
    );
  }

  // Not authenticated - render with PublicHeader/PublicFooter
  return (
    <div className="min-h-screen flex flex-col bg-[var(--color-secondary-bg)]">
      <PublicHeader />

      <main className="flex-1">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-[var(--color-surface-text)] mb-2">{t('marketplace.title')}</h1>
            <p className="text-lg text-[var(--color-secondary-text)]">
              {t('roadmapMarketplace.subtitle')}
            </p>
          </div>

          {/* Main Content - Reuse the same content structure */}
          <div>
          {/* Search and Filters */}
          <div className="mb-6">
            <div className="flex flex-wrap gap-4 items-end">
              {/* Search Bar */}
              <div className="relative flex-1 min-w-64 max-w-md">
                <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-1">
                  {t('marketplace.search')}
                </label>
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[var(--color-secondary-text)] opacity-50" />
                  <input
                    type="text"
                    placeholder={t('roadmapMarketplace.searchTemplates')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:ring-2 focus:ring-[var(--color-accent-bg)] focus:border-transparent"
                  />
                </div>
              </div>
              
              {/* Template Type Filter - HIDDEN: Marketplace only shows ROADMAP templates */}
              {/* <div className="flex-1 min-w-48">
                <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-1">
                  {t('marketplace.templateType')}
                </label>
                <select
                  value={selectedTemplateType}
                  onChange={(e) => setSelectedTemplateType(e.target.value)}
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:ring-2 focus:ring-[var(--color-accent-bg)] focus:border-transparent"
                >
                  <option value="">{t('marketplace.allTypes')}</option>
                  {allTemplateTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div> */}
              
              {/* Industry Filter */}
              <div className="flex-1 min-w-48">
                <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-1">
                  {t('roadmapMarketplace.filterByIndustry')}
                </label>
                <select
                  value={selectedIndustry}
                  onChange={(e) => setSelectedIndustry(e.target.value)}
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:ring-2 focus:ring-[var(--color-accent-bg)] focus:border-transparent"
                >
                  <option value="">{t('roadmapMarketplace.allIndustries')}</option>
                  {availableIndustries.map((industry) => (
                    <option key={industry} value={industry}>
                      {industry}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Functional Area Filter */}
              <div className="flex-1 min-w-48">
                <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-1">
                  {t('roadmapMarketplace.filterByFunctionalArea')}
                </label>
                <select
                  value={selectedFunctionalArea}
                  onChange={(e) => setSelectedFunctionalArea(e.target.value)}
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:ring-2 focus:ring-[var(--color-accent-bg)] focus:border-transparent"
                >
                  <option value="">{t('roadmapMarketplace.allFunctionalAreas')}</option>
                  {availableFunctionalAreas.map((area) => (
                    <option key={area} value={area}>
                      {area}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Clear Filters Button */}
              {(selectedIndustry !== '' || selectedFunctionalArea !== '' || searchTerm !== '') && (
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedIndustry('');
                      setSelectedFunctionalArea('');
                    }}
                    className="px-4 py-2 text-sm text-[var(--color-secondary-text)] hover:text-[var(--color-surface-text)] border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-accent-bg)]/20 transition-colors"
                  >
                    {t('roadmapMarketplace.clearFilters')}
                  </button>
                </div>
              )}
            </div>
          </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-[var(--color-secondary-text)] text-lg mb-4">{t('roadmapMarketplace.noTemplatesFound')}</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredTemplates.map((template) => {
              const metadata = template.content?.template_metadata;
              const hierarchy = template.content?.hierarchy;
              
              // Recursively count all projects including nested ones
              const countProjectsRecursively = (projects: any[]): number => {
                if (!projects || !Array.isArray(projects)) return 0;
                return projects.reduce((total, project) => {
                  const nestedCount = project.projects && Array.isArray(project.projects) 
                    ? countProjectsRecursively(project.projects) 
                    : 0;
                  return total + 1 + nestedCount; // Count this project + all nested projects
                }, 0);
              };
              
              const projectCount = hierarchy?.visions?.reduce((total, vision) => 
                total + vision.objectives?.reduce((objTotal, objective) => 
                  objTotal + objective.strategies?.reduce((strTotal, strategy) => 
                    strTotal + countProjectsRecursively(strategy.projects || []), 0), 0), 0);

              // Determine border color based on marketplace visibility
              const borderColor = template.marketplace_visibility === 'PUBLIC' 
                ? 'border-t-4 border-t-green-500' 
                : template.marketplace_visibility === 'PRIVATE'
                ? 'border-t-4 border-t-orange-500'
                : 'border-t-4 border-t-gray-300'; // Default for DRAFT or undefined

              return (
                <div
                  key={template.id}
                  className={`border border-[var(--color-border)] ${borderColor} rounded-lg hover:shadow-md transition-shadow cursor-pointer`}
                  onClick={() => handleView(template)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleView(template);
                    }
                  }}
                >
                  {/* Template Header */}
                  <div className={`p-4 border-b ${
                    template.marketplace_visibility === 'PUBLIC' 
                      ? 'border-b-green-200' 
                      : template.marketplace_visibility === 'PRIVATE'
                      ? 'border-b-orange-200'
                      : 'border-b-gray-100'
                  }`}>
                    <h3 className="font-semibold text-lg text-[var(--color-surface-text)] mb-2 line-clamp-2 hover:text-[var(--color-accent-bg)] transition-colors">
                      {template.name}
                    </h3>
                    <p className="text-[var(--color-secondary-text)] text-sm line-clamp-3">
                      {template.description || metadata?.description}
                    </p>
                    <div className="text-sm text-[var(--color-secondary-text)] mt-3">
                      <span className="font-medium text-[var(--color-surface-text)]">
                        {t('roadmapMarketplace.publisher') || 'Publisher'}:
                      </span>{' '}
                      {getPublisherDisplay(template)}
                    </div>
                  </div>

                  {/* Template Details */}
                  <div className="p-4">
                    <div className="space-y-2 text-sm">
                      {metadata?.industry && (
                        <div className="flex items-center">
                          <span className="text-[var(--color-secondary-text)] opacity-75 w-20">{t('roadmapMarketplace.industry')}</span>
                          <span className="text-[var(--color-surface-text)]">{metadata.industry}</span>
                        </div>
                      )}
                      {metadata?.functional_area && (
                        <div className="flex items-center">
                          <span className="text-[var(--color-secondary-text)] opacity-75 w-20">{t('roadmapMarketplace.area')}</span>
                          <span className="text-[var(--color-surface-text)]">{metadata.functional_area}</span>
                        </div>
                      )}
                      <div className="flex items-center">
                        <span className="text-[var(--color-secondary-text)] opacity-75 w-20">{t('roadmapMarketplace.complexity')}</span>
                        <span className="text-[var(--color-surface-text)]">{metadata?.complexity_level || t('common.notAvailable') || 'N/A'}/5</span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-[var(--color-secondary-text)] opacity-75 w-20">{t('roadmapMarketplace.projects')}</span>
                        <span className="text-[var(--color-surface-text)] font-medium">{projectCount}</span>
                      </div>
                      {metadata?.estimated_duration_months && (
                        <div className="flex items-center">
                          <span className="text-[var(--color-secondary-text)] opacity-75 w-20">{t('roadmapMarketplace.duration')}</span>
                          <span className="text-[var(--color-surface-text)]">{metadata.estimated_duration_months} {t('roadmapMarketplace.months')}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Template Actions */}
                  <div className="p-4 border-t border-[var(--color-border)] flex justify-between items-center">
                    {user ? (
                      <>
                        <div className="flex space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleView(template);
                            }}
                            className="p-2 text-[var(--color-secondary-text)] hover:text-[var(--color-accent-bg)] hover:bg-[var(--color-accent-bg)]/20 rounded-lg transition-colors"
                            title="View"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          {/* Only show download button if template is PUBLIC, or PRIVATE with access, or user is owner */}
                          {(template.marketplace_visibility === 'PUBLIC' || 
                            (template.marketplace_visibility === 'PRIVATE' && (template.created_by === user.id || templateAccess[template.id])) ||
                            !template.marketplace_visibility) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSaveToMyRoadmaps(template);
                              }}
                              disabled={savingTemplate === template.id}
                              className="p-2 text-[var(--color-secondary-text)] hover:text-[var(--color-success)] hover:bg-[var(--color-success)]/20 rounded-lg transition-colors disabled:opacity-50"
                              title={t('roadmapDetail.saveToMyRoadmaps')}
                            >
                              {savingTemplate === template.id ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-500"></div>
                              ) : (
                                <ArrowDownTrayIcon className="h-4 w-4" />
                              )}
                            </button>
                          )}
                        </div>
                        
                        <div className="flex items-center text-yellow-500">
                          <StarIcon className="h-4 w-4" />
                          <span className="text-sm font-medium ml-1">4.5</span>
                        </div>
                      </>
                    ) : (
                      <div className="w-full text-center">
                        <p className="text-sm text-[var(--color-secondary-text)]">
                          Login required to view and download
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
          </div>
        </div>
      </main>

    </div>
  );
};